import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Plus, Trash2 } from "lucide-react";
import {
  Button, Card, Field, Input, Pill, Select, Spinner, Textarea,
} from "@/components/ui";

// =============================================================================
// SupplierRateExtractionReview — T-008
// Shared review table for AI-extracted supplier rate cards. Used by:
//   • /rates/import-pdf  (saves the reviewed lines as a new permanent rate_card)
//   • QuoteBuilder       (inserts reviewed lines into a quote section, with an
//                         optional "also save as rate_card" path)
//
// Soft validation per James's call (T-008): blanks are allowed on category/unit;
// rows without a positive cost stay visible but disabled. Manager edits inline.
// =============================================================================

// Mirror migration 017 enums.
export const RATE_CATEGORIES = [
  "labour", "plant", "materials", "subcontract", "lafha", "markup", "fixed", "other",
] as const;
export const RATE_UNITS = [
  "hour", "day", "week", "month", "metre", "square_metre", "cubic_metre",
  "each", "per_site", "lump_sum", "percent",
] as const;
export type RateCategory = typeof RATE_CATEGORIES[number];
export type RateUnit     = typeof RATE_UNITS[number];

export interface ExtractedLine {
  display_name: string;
  line_code: string;
  category: RateCategory | null;
  unit: RateUnit | null;
  cost_price_ex_gst: number | null;
  notes: string;
  confidence: number;
  // client-side only
  _id: string;
  _enabled: boolean;
}

export interface ExtractedSupplierContact {
  legal_name?: string;
  trading_name?: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  primary_contact_phone?: string;
  billing_email?: string;
  address_line1?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  payment_terms_days_detected?: number;
}

export interface ExtractedCardMetadata {
  supplier_name_detected?: string;
  supplier_abn?: string;
  effective_from?: string;
  effective_until?: string;
  currency_assumed_aud: boolean;
  gst_treatment: "ex_gst" | "inc_gst" | "unclear";
  card_title?: string;
  supplier_contact?: ExtractedSupplierContact;
}

export interface ExtractedSummary {
  total_lines_extracted: number;
  pages_seen?: number;
  overall_confidence: number;
  warnings?: string[];
}

export interface ExtractionPayload {
  card_metadata: ExtractedCardMetadata;
  lines: Omit<ExtractedLine, "_id" | "_enabled">[];
  summary: ExtractedSummary;
}

interface Props {
  payload: ExtractionPayload;
  loading?: boolean;
  /** Called when the user clicks Confirm. Returns only enabled lines. */
  onConfirm: (lines: ExtractedLine[], metadata: ExtractedCardMetadata) => void;
  onCancel?: () => void;
  confirmLabel?: string;
  /** Hide the metadata card (used when the caller already has supplier info, e.g. QuoteBuilder). */
  hideMetadata?: boolean;
}

export default function SupplierRateExtractionReview({
  payload, loading, onConfirm, onCancel,
  confirmLabel = "Confirm & save",
  hideMetadata = false,
}: Props) {
  const [lines, setLines] = useState<ExtractedLine[]>(() =>
    payload.lines.map((l, i) => ({
      ...l,
      category: (l.category && (RATE_CATEGORIES as readonly string[]).includes(l.category))
        ? (l.category as RateCategory) : null,
      unit:     (l.unit     && (RATE_UNITS as readonly string[]).includes(l.unit))
        ? (l.unit as RateUnit) : null,
      notes:    l.notes ?? "",
      _id:      `r${i}-${l.line_code || "row"}`,
      _enabled: (l.cost_price_ex_gst ?? 0) > 0,
    })),
  );
  const [metadata, setMetadata] = useState<ExtractedCardMetadata>(payload.card_metadata);

  const enabledCount = useMemo(() => lines.filter(l => l._enabled).length, [lines]);

  function patchLine(id: string, patch: Partial<ExtractedLine>) {
    setLines(prev => prev.map(l => l._id === id ? { ...l, ...patch } : l));
  }
  function deleteLine(id: string) {
    setLines(prev => prev.filter(l => l._id !== id));
  }
  function addBlank() {
    const id = `new-${Date.now()}`;
    setLines(prev => [...prev, {
      _id: id, _enabled: true,
      display_name: "", line_code: "",
      category: null, unit: null,
      cost_price_ex_gst: null, notes: "",
      confidence: 1,
    }]);
  }

  // Confidence-based row tinting (border colour only — kept subtle).
  function rowTone(conf: number) {
    if (conf >= 0.85) return "border-line";
    if (conf >= 0.6)  return "border-amber-400/40";
    return "border-lost/40";
  }

  return (
    <div className="space-y-4">
      {!hideMetadata && (
        <Card>
          <div className="p-4 space-y-3">
            <div className="text-[12px] font-medium text-text-muted uppercase tracking-wider">
              Card metadata
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Supplier name (detected)">
                <Input
                  value={metadata.supplier_name_detected ?? ""}
                  onChange={e => setMetadata(m => ({ ...m, supplier_name_detected: e.target.value }))}
                  placeholder="—"
                />
              </Field>
              <Field label="Effective from">
                <Input
                  type="date"
                  value={metadata.effective_from ?? ""}
                  onChange={e => setMetadata(m => ({ ...m, effective_from: e.target.value || undefined }))}
                />
              </Field>
              <Field label="GST treatment">
                <Select
                  value={metadata.gst_treatment}
                  onChange={e => setMetadata(m => ({ ...m, gst_treatment: e.target.value as ExtractedCardMetadata["gst_treatment"] }))}
                >
                  <option value="ex_gst">Ex-GST</option>
                  <option value="inc_gst">Inc-GST</option>
                  <option value="unclear">Unclear</option>
                </Select>
              </Field>
            </div>
          </div>
        </Card>
      )}

      {payload.summary.warnings && payload.summary.warnings.length > 0 && (
        <div className="rounded border border-amber-400/30 bg-amber-400/[0.05] px-3 py-2 text-[12px] text-amber-200">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={14}/>
            <span className="font-medium">Worth reviewing:</span>
          </div>
          <ul className="list-disc pl-5 space-y-0.5">
            {payload.summary.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <Card>
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[12px] font-medium text-text-muted uppercase tracking-wider">
              Extracted lines · {enabledCount} of {lines.length} enabled
            </div>
            <Button variant="secondary" icon={Plus} onClick={addBlank}>Add blank line</Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-text-muted">
                  <th className="px-2 py-1 w-10"></th>
                  <th className="px-2 py-1">Code</th>
                  <th className="px-2 py-1 min-w-[200px]">Description</th>
                  <th className="px-2 py-1">Category</th>
                  <th className="px-2 py-1">Unit</th>
                  <th className="px-2 py-1 text-right">Cost ex-GST</th>
                  <th className="px-2 py-1 min-w-[200px]">Notes</th>
                  <th className="px-2 py-1 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l._id} className={`border-t ${rowTone(l.confidence)} ${!l._enabled ? "opacity-40" : ""}`}>
                    <td className="px-2 py-1 align-top pt-3">
                      <input
                        type="checkbox"
                        checked={l._enabled}
                        onChange={e => patchLine(l._id, { _enabled: e.target.checked })}
                        title="Include this line in the import"
                      />
                    </td>
                    <td className="px-2 py-1 align-top">
                      <Input
                        value={l.line_code}
                        mono
                        onChange={e => patchLine(l._id, {
                          line_code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 24),
                        })}
                      />
                    </td>
                    <td className="px-2 py-1 align-top">
                      <Input
                        value={l.display_name}
                        onChange={e => patchLine(l._id, { display_name: e.target.value })}
                        title={l.display_name || undefined}   /* T-185 — full text on hover when column truncates */
                      />
                    </td>
                    <td className="px-2 py-1 align-top">
                      <Select
                        value={l.category ?? ""}
                        onChange={e => patchLine(l._id, { category: (e.target.value || null) as RateCategory | null })}
                      >
                        <option value="">—</option>
                        {RATE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </Select>
                    </td>
                    <td className="px-2 py-1 align-top">
                      <Select
                        value={l.unit ?? ""}
                        onChange={e => patchLine(l._id, { unit: (e.target.value || null) as RateUnit | null })}
                      >
                        <option value="">—</option>
                        {RATE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                      </Select>
                    </td>
                    <td className="px-2 py-1 align-top">
                      <Input
                        type="number"
                        step="0.01"
                        value={l.cost_price_ex_gst ?? ""}
                        onChange={e => patchLine(l._id, {
                          cost_price_ex_gst: e.target.value === "" ? null : Number(e.target.value),
                        })}
                        className="text-right"
                      />
                    </td>
                    <td className="px-2 py-1 align-top">
                      <Textarea
                        rows={1}
                        value={l.notes}
                        onChange={e => patchLine(l._id, { notes: e.target.value })}
                        title={l.notes || undefined}   /* T-185 — full text on hover when truncated */
                      />
                    </td>
                    <td className="px-2 py-1 align-top pt-3">
                      <button
                        onClick={() => deleteLine(l._id)}
                        className="text-text-muted hover:text-lost"
                        title="Delete row"
                        type="button"
                      ><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <div className="text-[11px] text-text-muted flex items-center gap-2">
          <CheckCircle2 size={14} className="text-accent"/>
          Overall AI confidence: <Pill label={`${Math.round(payload.summary.overall_confidence * 100)}%`} small />
          {payload.summary.pages_seen ? <span>· {payload.summary.pages_seen} pages</span> : null}
        </div>
        <div className="flex gap-2">
          {onCancel && <Button variant="secondary" onClick={onCancel}>Cancel</Button>}
          <Button
            disabled={!!loading || enabledCount === 0}
            onClick={() => onConfirm(lines.filter(l => l._enabled), metadata)}
          >
            {loading ? <Spinner size={14}/> : `${confirmLabel} (${enabledCount})`}
          </Button>
        </div>
      </div>
    </div>
  );
}
